import { ArrowForward, SignalWifiOff, Wifi } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { Popup } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import { formatDateTime } from 'src/utils/dateHelper';
import { Sensor } from 'switch-shared/modules/crud/sensor/sensor.entity';

import pages from '../../../router/routes';

import './CustomPopup.css';

interface Props {
  sensor: Sensor;
}

type DisplayField = {
  label: string;
  value: string;
};

const SensorPopup = ({ sensor }: Props) => {
  /* Hooks & Use Effects */
  const theme = useTheme();
  const navigate = useNavigate();
  const { getApplicationScopedChildPath } = useAdminUrlNavigation();

  /* States */

  /* API Calls */

  /* Functions & Functional Components */

  // Determine if sensor is online based on lastSeen (e.g., within last 24 hours)
  const isOnline = sensor.lastSeen
    ? dayjs().diff(dayjs(sensor.lastSeen), 'hour') < 24
    : false;

  const primaryFields: DisplayField[] = [
    {
      label: 'Battery',
      value:
        sensor.batteryPcnt !== undefined && sensor.batteryPcnt !== null
          ? `${(sensor.batteryPcnt * 100).toFixed(0)}%`
          : 'N/A',
    },
    {
      label: 'Last Seen',
      value: sensor.lastSeen
        ? formatDateTime(sensor.lastSeen)
        : 'No communication',
    },
  ];

  const additionalReadings: DisplayField[] = [];

  const pressure = sensor.pressureBar;
  if (pressure !== undefined && pressure !== null) {
    additionalReadings.push({
      label: 'Pressure',
      value: `${pressure.toFixed(2)} bar`,
    });
  }

  const temperature = sensor.temperatureC;
  if (temperature !== undefined && temperature !== null) {
    additionalReadings.push({
      label: 'Temperature',
      value: `${temperature.toFixed(1)} °C`,
    });
  }

  const batteryVoltage = sensor.batteryV;
  if (batteryVoltage !== undefined && batteryVoltage !== null) {
    additionalReadings.push({
      label: 'Battery Voltage',
      value: `${batteryVoltage.toFixed(2)} V`,
    });
  }

  const altitude = sensor.altitudeM;
  if (altitude !== undefined && altitude !== null) {
    additionalReadings.push({
      label: 'Altitude',
      value: `${altitude.toFixed(1)} m`,
    });
  }

  const latitude = sensor.latitude;
  const longitude = sensor.longitude;
  if (
    latitude !== undefined &&
    latitude !== null &&
    longitude !== undefined &&
    longitude !== null
  ) {
    additionalReadings.push({
      label: 'Location',
      value: `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`,
    });
  }

  const rssi = sensor.rssiDBm;
  if (rssi !== undefined && rssi !== null) {
    additionalReadings.push({
      label: 'Signal Strength',
      value: `${rssi.toFixed(0)} dBm`,
    });
  }

  if (sensor.gatewayId) {
    additionalReadings.push({
      label: 'Gateway',
      value: sensor.gatewayId,
    });
  }

  const fieldsToRender = [...primaryFields, ...additionalReadings];

  return (
    <Popup>
      <Card
        sx={{
          minWidth: 360,
          maxWidth: 450,
          backgroundColor: 'rgba(0,0,0,0)',
          borderRadius: '0',
        }}
      >
        {/* Header with sensor name and action button */}
        <CardHeader
          title={sensor.name}
          titleTypographyProps={{
            variant: 'h3',
            fontWeight: 'bold',
            lineHeight: '1.2',
            sx: { color: theme.colors.primary.main },
          }}
          action={
            <IconButton
              color='primary'
              title='Open sensor'
              style={{ position: 'absolute', right: 0 }}
              onClick={() =>
                navigate(
                  getApplicationScopedChildPath(
                    'sensors',
                    sensor._id,
                    pages.sensors.view.path.replace(':id', sensor._id)
                  )
                )
              }
            >
              <ArrowForward />
            </IconButton>
          }
          sx={{ padding: 0, position: 'relative', paddingRight: 8 }}
        />

        {/* Status Tags Row - Right under the name */}
        <Stack
          direction='row'
          spacing={1}
          sx={{ mb: 2, mt: 1 }}
          flexWrap='wrap'
          useFlexGap
        >
          {/* Online/Offline Status */}
          <Tooltip
            title={
              sensor.lastSeen !== undefined
                ? `Last seen: ${formatDateTime(sensor.lastSeen)}`
                : 'No communication'
            }
          >
            <Chip
              icon={
                isOnline ? (
                  <Wifi sx={{ color: 'inherit !important' }} />
                ) : (
                  <SignalWifiOff sx={{ color: 'inherit !important' }} />
                )
              }
              label={isOnline ? 'Online' : 'Offline'}
              size='small'
              sx={{
                backgroundColor: isOnline
                  ? theme.tokens.colors.success
                  : theme.tokens.colors.destructive,
              }}
            />
          </Tooltip>

          {/* Sensor Type Badge */}
          <Chip
            label='Sensor'
            size='small'
            sx={{
              backgroundColor: theme.colors.neutral.grey,
            }}
          />
        </Stack>

        <CardContent sx={{ padding: 0, paddingBottom: '0 !important' }}>
          {/* Two-column layout for data fields */}
          <Grid container spacing={2}>
            {fieldsToRender.map((field) => (
              <Grid item xs={6} key={field.label}>
                <Box>
                  <Typography
                    variant='h5'
                    sx={{ color: theme.colors.primary.main }}
                  >
                    {field.label}
                  </Typography>
                  <Typography variant='subtitle1'>{field.value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Popup>
  );
};

export default SensorPopup;
